import { selectionText } from './columnFilters'
import { fullDate } from './reportDates'
import { cellText, leaveTypesIn, type PayPeriodFilters, type PayPeriodRow } from './payPeriodGridFigures'
import type { ExportTable } from './reportExport'

export type DayHeading = { date: string; weekday: string; month: string; day: number }

// "Mon Sep 22", the heading over one day of the grid.
export const dayHeading = (day: DayHeading) => `${day.weekday} ${day.month.slice(0, 3)} ${day.day}`

export function payPeriodGridTable(
  rows: PayPeriodRow[],
  input: { periodLabel: string; days: DayHeading[]; filters: PayPeriodFilters; teams: Map<string, string>; generatedAt: Date },
): ExportTable {
  const { filters, days } = input
  const leaveTypes = leaveTypesIn(rows)
  const dayColumns = days.length
  const totalOf = (pick: (row: PayPeriodRow) => number) => rows.reduce((sum, row) => sum + pick(row), 0)

  return {
    title: 'Pay Period Schedule',
    parameters: [
      { label: 'Pay period', value: `${input.periodLabel} (${fullDate(days[0].date)} to ${fullDate(days[days.length - 1].date)})` },
      { label: 'Unit', value: selectionText(filters.unit, (id) => input.teams.get(id) ?? 'Unknown unit') },
      { label: 'Shift', value: selectionText(filters.shift) },
      { label: 'Title', value: selectionText(filters.title) },
      ...(filters.employee ? [{ label: 'Name', value: filters.employee.length === 1 ? '1 selected' : `${filters.employee.length} selected` }] : []),
    ],
    generatedAt: input.generatedAt,
    // Fourteen days across, which only a wide page holds.
    compact: true,
    columns: [
      { header: 'Unit', width: 14 },
      { header: 'Shift', width: 14 },
      { header: 'Title', width: 22 },
      { header: 'Name', width: 22 },
      ...days.map((day) => ({ header: dayHeading(day), width: 12 })),
      { header: 'Work days', numeric: true },
      { header: 'Days off', numeric: true },
      ...leaveTypes.map((type) => ({ header: type, numeric: true })),
      { header: 'Other', numeric: true },
    ],
    rows: rows.map((row) => [
      row.unit,
      row.shift,
      row.title,
      row.employee,
      ...row.cells.map((cell) => cellText(cell)),
      row.workDays,
      row.daysOff,
      ...leaveTypes.map((type) => row.leave[type] ?? 0),
      row.otherDays,
    ]),
    totals: {
      label: 'Total',
      values: [
        null,
        null,
        null,
        null,
        ...Array.from({ length: dayColumns }, () => null),
        totalOf((row) => row.workDays),
        totalOf((row) => row.daysOff),
        ...leaveTypes.map((type) => totalOf((row) => row.leave[type] ?? 0)),
        totalOf((row) => row.otherDays),
      ],
    },
  }
}

